import { useState, useRef, useCallback } from 'react';

const transformations = [
  {
    id: 1,
    title: 'Kitchen Deep Clean',
    description: 'Grease, stains and grime completely removed',
    before: 'https://public.readdy.ai/ai/img_res/52c29753671bf4b1db053a4c1b942b36.jpg',
    after: 'https://readdy.ai/api/search-image?query=spotless%20bright%20clean%20kitchen%20shiny%20countertops%20organized%20sparkling%20surfaces%20professional%20cleaning%20result%20white%20modern%20kitchen%20after%20cleaning&width=700&height=500&seq=ba_kitchen_after_01&orientation=landscape',
  },
  {
    id: 2,
    title: 'Bathroom Restoration',
    description: 'Mold, soap scum and limescale fully eliminated',
    before: 'https://readdy.ai/api/search-image?query=dirty%20bathroom%20with%20mold%20soap%20scum%20limescale%20stained%20tiles%20grimy%20toilet%20dark%20neglected%20bathroom%20before%20cleaning%20realistic&width=700&height=500&seq=ba_bath_before_02&orientation=landscape',
    after: 'https://readdy.ai/api/search-image?query=sparkling%20clean%20bright%20white%20bathroom%20shiny%20tiles%20polished%20fixtures%20fresh%20towels%20professional%20cleaning%20result%20modern%20bathroom%20after%20cleaning&width=700&height=500&seq=ba_bath_after_02&orientation=landscape',
  },
  {
    id: 3,
    title: 'Living Room Refresh',
    description: 'Dust, pet hair and stains completely gone',
    before: 'https://readdy.ai/api/search-image?query=messy%20dusty%20living%20room%20cluttered%20furniture%20stained%20carpet%20pet%20hair%20dirty%20windows%20neglected%20home%20before%20cleaning%20realistic%20photo&width=700&height=500&seq=ba_living_before_03&orientation=landscape',
    after: 'https://readdy.ai/api/search-image?query=clean%20bright%20organized%20living%20room%20fresh%20vacuumed%20carpet%20polished%20furniture%20clear%20windows%20professional%20cleaning%20result%20cozy%20home%20after%20cleaning&width=700&height=500&seq=ba_living_after_03&orientation=landscape',
  },
  {
    id: 4,
    title: 'Move-Out Cleaning',
    description: 'Full property restored to pristine condition',
    before: 'https://readdy.ai/api/search-image?query=empty%20dirty%20apartment%20after%20move%20out%20stained%20walls%20dirty%20floors%20grimy%20windows%20neglected%20rooms%20before%20cleaning%20realistic%20photo&width=700&height=500&seq=ba_moveout_before_04&orientation=landscape',
    after: 'https://readdy.ai/api/search-image?query=empty%20spotless%20bright%20apartment%20after%20professional%20move%20out%20cleaning%20clean%20floors%20white%20walls%20shiny%20windows%20pristine%20condition%20after%20cleaning&width=700&height=500&seq=ba_moveout_after_04&orientation=landscape',
  },
];

function SliderCard({ item }: { item: typeof transformations[0] }) {
  const [sliderPos, setSliderPos] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const updateSlider = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.min(Math.max((x / rect.width) * 100, 2), 98);
    setSliderPos(pct);
  }, []);

  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    updateSlider(e.clientX);
  }, [isDragging, updateSlider]);

  const onMouseUp = () => setIsDragging(false);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    updateSlider(e.touches[0].clientX);
  }, [updateSlider]);

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
      <div
        ref={containerRef}
        className="relative w-full h-64 select-none cursor-col-resize overflow-hidden"
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onTouchMove={onTouchMove}
        onTouchEnd={onMouseUp}
      >
        {/* After image (base) */}
        <img
          src={item.after}
          alt={`After - ${item.title}`}
          className="absolute inset-0 w-full h-full object-cover object-top"
          draggable={false}
        />

        {/* Before image (clipped) */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ width: `${sliderPos}%` }}
        >
          <img
            src={item.before}
            alt={`Before - ${item.title}`}
            className="absolute inset-0 w-full h-full object-cover object-top"
            style={{ width: `${10000 / sliderPos}%`, maxWidth: 'none' }}
            draggable={false}
          />
        </div>

        {/* Divider line */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg z-10"
          style={{ left: `${sliderPos}%` }}
        />

        {/* Drag handle */}
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-20 w-10 h-10 bg-white rounded-full shadow-xl flex items-center justify-center cursor-col-resize"
          style={{ left: `${sliderPos}%` }}
          onMouseDown={onMouseDown}
          onTouchStart={() => setIsDragging(true)}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-arrow-left-right-line text-teal-600 text-base"></i>
          </div>
        </div>

        {/* Labels */}
        <div className="absolute top-3 left-3 z-10 bg-black/60 text-white text-xs font-semibold px-3 py-1 rounded-full">
          BEFORE
        </div>
        <div className="absolute top-3 right-3 z-10 bg-teal-600/90 text-white text-xs font-semibold px-3 py-1 rounded-full">
          AFTER
        </div>
      </div>

      <div className="p-5">
        <h3 className="text-base font-semibold text-gray-800 mb-1">{item.title}</h3>
        <p className="text-sm text-gray-500">{item.description}</p>
      </div>
    </div>
  );
}

export default function BeforeAfter() {
  return (
    <section id="before-after" className="py-16 sm:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-14">
          <span className="inline-block bg-teal-50 text-teal-600 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            Real Results
          </span>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            See the Difference We Make
          </h2>
          <p className="text-gray-500 max-w-xl mx-auto text-sm leading-relaxed">
            Drag the slider on each photo to reveal the transformation. Every result is from a real cleaning job — no filters, no tricks.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
          {transformations.map((item) => (
            <SliderCard key={item.id} item={item} />
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-10 sm:mt-14">
          <p className="text-gray-500 text-sm mb-5">Ready to see these results in your home?</p>
          <button
            onClick={() => {
              const el = document.getElementById('contact');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-teal-600 text-white px-8 py-3 rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            Book Your Cleaning Today
          </button>
        </div>
      </div>
    </section>
  );
}
